import { useCallback, useState } from 'react';
import type { ChatSessionType } from '@demind-inc/core';

import type {
  ChatV2QuickActionCategory,
  ChatV2QuickActionItem,
  ChatV2QuickActionSubItem,
} from '../../Chat/ChatV2';
import { selectedDateAtom } from '../../../data-access';
import { useRecoilValue } from 'recoil';

interface UseQuickActionSelectionProps {
  onPlanEntry: (planType: ChatSessionType, targetDate?: string, prompt?: string) => void;
  onStartGeneralChat: (prompt: string) => void;
  onPrefill: (text: string) => void;
  onPlanMyEntireDay?: () => void;
  /** Dev-only debug quick actions ('debug-mock-rich-context' /
   * 'debug-mock-replan' / 'debug-mock-plan-status') — see AssistantChatProvider's
   * injectDebugMockConversation/injectDebugMockReplanConversation/
   * injectDebugMockPlanStatusConversation. */
  onDebugMockAction?: (id: string) => void;
}

export const useQuickActionSelection = ({
  onPlanEntry,
  onStartGeneralChat,
  onPrefill,
  onPlanMyEntireDay,
  onDebugMockAction,
}: UseQuickActionSelectionProps) => {
  const [selectedCategory, setSelectedCategory] = useState<ChatV2QuickActionCategory | null>(null);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const selectedDate = useRecoilValue(selectedDateAtom);

  const handleCategoryPress = useCallback((category: ChatV2QuickActionCategory) => {
    setSelectedCategory(category);
    setExpandedItemId(null);
  }, []);

  const triggerAction = useCallback(
    ({
      id,
      prompt,
      planType,
      prefillText,
    }: Pick<ChatV2QuickActionItem, 'id' | 'prompt' | 'planType' | 'prefillText'>) => {
      if (prefillText) {
        onPrefill(prefillText);
        return;
      }

      if (id === 'tell-me-what-to-do') {
        onPlanEntry('recommendation');
        return;
      }

      if (id === 'plan-my-entire-day') {
        onPlanMyEntireDay?.();
        return;
      }

      if (
        id === 'debug-mock-rich-context' ||
        id === 'debug-mock-replan' ||
        id === 'debug-mock-plan-status'
      ) {
        onDebugMockAction?.(id);
        return;
      }

      if (planType) {
        onPlanEntry(planType, selectedDate.format('YYYY-MM-DD'), prompt);
        return;
      }

      if (prompt) {
        onStartGeneralChat(prompt);
      }
    },
    [onPlanEntry, onPlanMyEntireDay, onPrefill, onStartGeneralChat, onDebugMockAction, selectedDate]
  );

  const handleItemPress = useCallback(
    (item: ChatV2QuickActionItem) => {
      if (item.subItems?.length) {
        setExpandedItemId((prev) => (prev === item.id ? null : item.id));
        return;
      }

      triggerAction(item);
      setExpandedItemId(null);
      setSelectedCategory(null);
    },
    [triggerAction]
  );

  const handleSubItemPress = useCallback(
    (subItem: ChatV2QuickActionSubItem) => {
      triggerAction({
        id: subItem.id,
        prompt: subItem.prompt,
        prefillText: subItem.prefillText,
      });
      setExpandedItemId(null);
      setSelectedCategory(null);
    },
    [triggerAction]
  );

  const resetSelection = useCallback(() => {
    setSelectedCategory(null);
    setExpandedItemId(null);
  }, []);

  return {
    selectedCategory,
    expandedItemId,
    handleCategoryPress,
    handleItemPress,
    handleSubItemPress,
    resetSelection,
  };
};
